<template>
    <div class="flex flex-col h-full gap-2">
        <div class="flex items-center gap-2">
            <date-range-selector v-model="dateRange" />
        </div>
        <ag-grid-vue
            class="flex-1 min-h-0"
            row-model-type="serverSide"
            :master-detail="true"
            :detail-row-auto-height="true"
            :detail-cell-renderer-params="detailCellRendererParams"
            :column-defs="eventColumns"
            :grid-options="eventGridOptions"
            :server-side-datasource="eventDataSource"
            :theme="agTheme"
        />
    </div>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import DateRangeSelector from '@/dateUtils/DateRangeSelector.vue'
import {getMaxDateRange, getPersistedRangeOr, persistentRange, updatePersistedRange} from '@/dateUtils/dateRange.service'
import {computed, ref} from 'vue'
import {
    ColDef,
    GetDetailRowDataParams,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IServerSideDatasource,
    IServerSideGetRowsParams,
} from 'ag-grid-community'
import {AuditLogEntry, EventEntry, FieldChange} from '@/types/AuditLog'
import AuditSubjectCell from '@/activity-log/AuditSubjectCell.vue'
import {actionColor, actionLabel, fetchAuditLog} from '@/activity-log/activity-log.service'
import {post} from '@/services/infrastructure/api.service'
import {toQueryRequest} from '@/query/AgGridAdapter'
import {ORG_SWITCHED} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import {Filters} from '@/query/Filters'

const gridApi = ref<GridApi | null>(null)
const {onEvent} = useEmitter()

const dateRange = computed({
    get: () => {
        if (!persistentRange.value) {
            updatePersistedRange(getPersistedRangeOr(getMaxDateRange()))
        }
        return persistentRange.value!
    },
    set: (range) => {
        updatePersistedRange(range)
        refresh()
    }
})

const refresh = () => gridApi.value?.refreshServerSide({ purge: true })

const eventDataSource = computed<IServerSideDatasource>(() => ({
    async getRows(params: IServerSideGetRowsParams) {
        const queryRequest = toQueryRequest(params.request)
        queryRequest.filters = {
            ...queryRequest.filters,
            createdAt: Filters.and([
                Filters.timestamp(dateRange.value.start.toISOString()).onOrAfter(),
                Filters.timestamp(dateRange.value.end.toISOString()).onOrBefore(),
            ])
        }
        const rows: EventEntry[] = await post('/event-log/:getrows', queryRequest) ?? []
        params.success({ rowData: rows })
    }
}))

const formatEventSubtype = (eventSubtype: string): string => eventSubtype

const eventColumns: ColDef[] = [
    {
        colId: 'createdAt',
        headerName: 'Dato',
        field: 'createdAt',
        width: 160,
        sortable: true,
        filter: false,
        initialSort: 'desc',
        cellRenderer: 'agGroupCellRenderer',
        valueFormatter: ({value}) => new Date(value).toLocaleString('no-NO', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        }),
    },
    {
        colId: 'actorDisplay',
        headerName: 'Bruker',
        field: 'actor.display',
        width: 200,
        filter: 'agTextColumnFilter',
        filterParams: { filterOptions: ['contains', 'equals'], debounceMs: 300 },
    },
    {
        colId: 'eventType',
        headerName: 'Type',
        field: 'eventType',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: { filterOptions: ['equals'], debounceMs: 300 },
    },
    {
        colId: 'eventSubtype',
        headerName: 'Hendelse',
        field: 'eventSubtype',
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: { filterOptions: ['contains', 'equals'], debounceMs: 300 },
        valueFormatter: ({value}) => formatEventSubtype(value),
    },
]

const auditColumns: ColDef[] = [
    {
        headerName: 'Handling',
        field: 'action',
        width: 110,
        cellRenderer: ({value}: ICellRendererParams) => {
            const span = document.createElement('span')
            span.textContent = actionLabel(value)
            span.style.cssText = `font-weight:500;${actionColor(value)}`
            return span
        },
    },
    {
        headerName: 'Entitet',
        field: 'subject.type',
        width: 160,
    },
    {
        headerName: 'Navn',
        field: 'subject.display',
        flex: 1,
        cellRenderer: AuditSubjectCell,
    },
    {
        headerName: 'Endringer',
        field: 'changes',
        flex: 2,
        autoHeight: true,
        wrapText: false,
        valueFormatter: ({value}: {value: FieldChange[] | null}) => {
            if (!value?.length) return ''
            return value.map(c => `${c.field}: ${c.from ?? '–'} → ${c.to ?? '–'}`).join(' | ')
        },
    },
]

const detailCellRendererParams = {
    detailGridOptions: {
        getRowId: ({data}: {data: AuditLogEntry}) => data.uuid,
        columnDefs: auditColumns,
        defaultColDef: {
            sortable: true,
            resizable: true,
            filter: false,
        },
    },
    getDetailRowData: async (params: GetDetailRowDataParams<EventEntry>) => {
        const rows = await fetchAuditLog({
            correlationId: params.data.correlationId,
            limit: 100,
        })
        params.successCallback(rows)
    },
}

const eventGridOptions: GridOptions = {
    getRowId: ({data}) => data.uuid,
    onGridReady: ({api}: GridReadyEvent) => { gridApi.value = api },
    isRowMaster: () => true,
    defaultColDef: {
        sortable: false,
        resizable: true,
        filter: false,
    },
}

onEvent(ORG_SWITCHED, refresh)
</script>

